import { useMemo } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import Admin from "../../views/admin/Admin";
import Articles from "../../views/articles/Articles";
import Settings from "../../views/settings/Settings";
import Layout from "../Layout";
import { AppRouteErrorBoundary } from "../components/AppErrorBoundary";
import { useAppSelector } from "../../redux/hooks";
import { getIsAdmin } from "../../redux/slices/env";

type Route = RouteObject & {
  path: string;
  admin?: boolean;
} & (
    {
      navLink: boolean;
      label: string;
    }
    | {
      navLink?: never;
      label?: never;
    }
  );

export function useAppRouter() {
  const isAdmin = useAppSelector(getIsAdmin);

  const routes = useMemo(() => {
    const allRoutes: Route[] = [
      {
        path: "/",
        element: (
          <Navigate
            to={{
              pathname: "/articles",
              search: window.location.search
            }}
            replace
          />
        ),
      },
      {
        path: "/articles",
        element: <Articles />,
        navLink: true,
        label: "Articles",
      },
      {
        path: "/articles/:articleId",
        element: <Articles />,
      },
      {
        path: "/settings",
        element: <Settings />,
        navLink: true,
        label: "Settings",
      },
      {
        path: "/admin",
        element: <Admin />,
        admin: true,
        navLink: true,
        label: "Admin",
      },
      {
        path: "/admin/shops",
        element: <Admin />,
        admin: true,
      },
      {
        path: "*",
        element: (
          <Navigate
            to={{
              pathname: "/articles",
              search: window.location.search
            }}
            replace
          />
        ),
      }
    ] as const;

    const routes = allRoutes.filter(route => {
      return route.admin ? isAdmin : true;
    });

    return routes;
  }, [isAdmin]);

  type AppNavLinks = Pick<
    Extract<Route, { navLink: boolean }>,
    "path" | "label"
  >;

  const navLinks = useMemo((): AppNavLinks[] => {
    return routes.filter((route) => {
      return !!route.navLink && !!route.label;
    }) as AppNavLinks[];
  }, [routes]);

  const router = useMemo(() => {
    return createBrowserRouter(
      [
        {
          path: "/",
          element: <Layout />,
          errorElement: <AppRouteErrorBoundary />,
          children: routes,
        },
      ],
      {
        future: {
          v7_normalizeFormMethod: true,
          v7_partialHydration: true,
          v7_fetcherPersist: true,
          v7_relativeSplatPath: true,
          v7_skipActionErrorRevalidation: true,
        },
      }
    );
  }, [routes]);

  return {
    router,
    navLinks
  };
}
